/**
 * Parse a Collins booking date (`YYYY-MM-DD`, optionally with a time part) at
 * local midnight. Returns null when the date is missing, malformed or not a
 * real calendar date, so callers never act on a guess: `new Date(2026, 1, 31)`
 * silently rolls into March, which would make an impossible date look like one
 * that has been and gone.
 */
function parseBookingDate(date: string | null | undefined): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? "");
  if (!parts) return null;

  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const parsed = new Date(year, month - 1, day, 0, 0, 0, 0);
  const isRealDate = parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;

  return isRealDate ? parsed : null;
}

/**
 * Whether the booking's date is behind us beyond any doubt.
 *
 * A date-only booking belongs to the venue's calendar; the browser only knows
 * the guest's. Time zones span 26 hours, so a guest's date can run a day ahead
 * of the venue's, and comparing the two directly would call a booking past
 * while the venue is still serving it. Yesterday is therefore left alone: only
 * a date no time zone could still call today counts as passed.
 */
function hasDatePassed(date: string | null | undefined): boolean {
  const bookingDate = parseBookingDate(date);
  if (bookingDate === null) return false;

  const now = new Date();
  const earliestToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
  return bookingDate < earliestToday;
}

/**
 * Whether the booking has already taken place, so it can take no further
 * pre-orders.
 *
 * The only evidence available is that DMN refused to hand the booking over and
 * that its date is unambiguously behind us. A refusal alone says no more than
 * "the guest cannot use the secure-booking page", hence the date requirement;
 * the date alone says nothing either, since Collins decides whether a
 * pre-order is allowed and a venue may legitimately still accept one.
 *
 * Deliberately *not* used: the booking's status. Collins' `status` records
 * where a booking sits in the venue's workflow, not whether the visit is over —
 * `complete` is `Booking::STATUS_CONFIRMED` (collins-api
 * `app/Models/Booking.php`), i.e. a live, confirmed booking, and dmn's
 * `PreorderManager` treats it as a *precondition* for pre-ordering. Reading it
 * as "finished" would refuse pre-orders to every confirmed booking. The set is
 * `new`, `in_progress`, `complete`, `rejected`, `lost`, `deleted`; none of them
 * means the meal has happened.
 *
 * This deliberately overrides an open pre-order window: a venue whose close
 * date outlives the booking leaves Collins accepting orders for a meal that has
 * already happened, and saying so is the point of this check. Collins' own
 * account of why pre-ordering is off (closed, limit reached, complete) is more
 * specific and is shown in preference to it by both flows.
 */
export function hasBookingTakenPlace(booking: {
  date?: string | null;
  /** DMN refused the booking itself (e.g. its deposit window has closed). */
  refused?: boolean;
}): boolean {
  return booking.refused === true && hasDatePassed(booking.date);
}
